// The product page's browser side (C8.2, 01.11.2026): strict TypeScript, compiled on build to wwwroot/js/site.js. The page is complete
// without it (the menu and every fold-out are <details>); this module only adds
//   - the reveal motion (not with prefers-reduced-motion) and the step highlight,
//   - the price calculator, which is hidden in the markup until this module can run it,
//   - closing the phone menu after a link.
// No tracker, no cookie, no storage, no request: it reads the markup and its data attributes. One init at the end.
// The packages' numbers are the control plane's settings (Steuerung:Billing:Plans), written by the server as data attributes: this module holds none.

/** A package of the calculator, read from the data attributes of its radio button. */
interface CalcPlan {
    readonly key: string;
    readonly name: string;
    /** The monthly base price; null = none. */
    readonly base: number | null;
    /** The users the base price includes. */
    readonly included: number;
    /** The price per further user (per user for a package without a base price); null = none. */
    readonly seat: number | null;
    /** The most users; null = no limit. */
    readonly max: number | null;
    /** The trial's address with this package chosen; null = none (on request). */
    readonly trial: string | null;
}

/** A data attribute as a number, or null when it is missing, empty or no number. */
function numberOf(value: string | undefined): number | null {
    if (value === undefined || value.trim() === "") {
        return null;
    }

    const number = Number(value);
    return Number.isFinite(number) ? number : null;
}

/** Fills the placeholders {0}, {1}, … of a template the markup carries. */
function fill(template: string, ...values: readonly string[]): string {
    return values.reduce((text, value, index) => text.split(`{${index}}`).join(value), template);
}

/** Reads a package from its radio button. */
function readPlan(radio: HTMLInputElement): CalcPlan {
    const data = radio.dataset;
    const trial = data["trial"];
    return {
        key: radio.value,
        name: data["name"] ?? radio.value,
        base: numberOf(data["base"]),
        included: numberOf(data["included"]) ?? 0,
        seat: numberOf(data["seat"]),
        max: numberOf(data["max"]),
        trial: trial ? trial : null,
    };
}

/** Whether the package has a price at all; one without is on request. */
function isPriced(plan: CalcPlan): boolean {
    return plan.base !== null || plan.seat !== null;
}

/** Whether the users fit into the package's limit. */
function fits(plan: CalcPlan, users: number): boolean {
    return users >= 1 && (plan.max === null || users <= plan.max);
}

/** The month's price: the base price with its included users plus every further user (or every user without a base price); null on request or when they do not fit. */
function monthly(plan: CalcPlan, users: number): number | null {
    if (!isPriced(plan) || !fits(plan, users)) {
        return null;
    }

    return plan.base !== null ? plan.base + Math.max(0, users - plan.included) * (plan.seat ?? 0) : users * (plan.seat ?? 0);
}

/** The package with the lowest month's price the users fit into; without one the first on request they fit into; else null. */
function cheapestFitting(plans: readonly CalcPlan[], users: number): CalcPlan | null {
    let cheapest: CalcPlan | null = null;
    let lowest = Number.POSITIVE_INFINITY;
    for (const plan of plans) {
        const price = monthly(plan, users);
        if (price !== null && price < lowest) {
            cheapest = plan;
            lowest = price;
        }
    }

    return cheapest ?? plans.find((plan) => !isPriced(plan) && fits(plan, users)) ?? null;
}

/** Whether the visitor asked for less motion. */
function prefersReducedMotion(): boolean {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/** Calls the callback for every element of the selector once it has come into view. */
class ViewWatcher {
    private readonly observer: IntersectionObserver;

    constructor(private readonly onEnter: (element: HTMLElement) => void, margin: string, private readonly once: boolean) {
        this.observer = new IntersectionObserver((entries) => this.handle(entries), { rootMargin: margin });
    }

    watch(elements: Iterable<HTMLElement>): void {
        for (const element of elements) {
            this.observer.observe(element);
        }
    }

    private handle(entries: IntersectionObserverEntry[]): void {
        for (const entry of entries) {
            if (!entry.isIntersecting) {
                continue;
            }

            this.onEnter(entry.target as HTMLElement);
            if (this.once) {
                this.observer.unobserve(entry.target);
            }
        }
    }
}

/** Lets the sections fade in while scrolling: `html.motion` hides `.reveal` until the element carries `is-in`. */
class RevealMotion {
    constructor(private readonly root: HTMLElement) {}

    start(): void {
        this.root.classList.add("motion");
        new ViewWatcher((element) => element.classList.add("is-in"), "0px 0px -10% 0px", true).watch(this.root.querySelectorAll<HTMLElement>(".reveal"));
    }
}

/** Marks the step that crosses the middle of the screen as `active` (the filled node and the dark heading). */
class StepHighlight {
    constructor(private readonly steps: readonly HTMLElement[]) {}

    start(): void {
        new ViewWatcher((element) => this.activate(element), "-45% 0px -45% 0px", false).watch(this.steps);
    }

    private activate(active: HTMLElement): void {
        for (const step of this.steps) {
            step.classList.toggle("active", step === active);
        }
    }
}

/** Closes the phone menu after a click on one of its links. */
class MenuCloser {
    constructor(private readonly menu: HTMLDetailsElement) {}

    start(): void {
        this.menu.addEventListener("click", (event) => {
            if ((event.target as HTMLElement).closest("a")) {
                this.menu.open = false;
            }
        });
    }
}

/** The number formatting of the page's language and the markup's templates ("{0} €" or "€{0}"). */
class Money {
    constructor(private readonly locale: string, private readonly template: string) {}

    format(value: number): string {
        const digits = Number.isInteger(value) ? 0 : 2;
        const text = new Intl.NumberFormat(this.locale, { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(value);
        return this.template.replace("{0}", text);
    }
}

/**
 * The calculator: package and users in, a month and a year out, the cheapest package that fits when the chosen one does not, and the trial with the
 * chosen package. Every text and number comes from the markup; nothing is written as HTML.
 */
class PriceCalculator {
    private readonly plans: readonly CalcPlan[];
    private readonly first: CalcPlan;
    private readonly radios: readonly HTMLInputElement[];
    private readonly range: HTMLInputElement;
    private readonly output: HTMLOutputElement;
    private readonly min: number;
    private readonly max: number;
    private readonly money: Money;

    constructor(private readonly root: HTMLElement) {
        this.radios = [...root.querySelectorAll<HTMLInputElement>("input[type=radio]")];
        this.plans = this.radios.map(readPlan);
        const first = this.plans[0];
        if (!first) {
            throw new Error("The calculator's markup has no package.");
        }

        this.first = first;
        this.range = this.required<HTMLInputElement>("input[type=range]");
        this.output = this.required<HTMLOutputElement>("[data-seats-out]");
        this.min = Number(root.dataset["min"]);
        this.max = Number(root.dataset["max"]);
        this.money = new Money(root.dataset["locale"] ?? "de-DE", root.dataset["money"] ?? "{0}");
    }

    start(): void {
        for (const radio of this.radios) {
            radio.addEventListener("change", () => this.render());
        }

        this.range.addEventListener("input", () => this.render());
        this.required<HTMLButtonElement>("[data-minus]").addEventListener("click", () => this.step(-1));
        this.required<HTMLButtonElement>("[data-plus]").addEventListener("click", () => this.step(1));
        this.required<HTMLButtonElement>("[data-suggest-btn]").addEventListener("click", () => this.choose(this.suggestion()));
        this.root.hidden = false;
        this.render();
    }

    private required<T extends HTMLElement>(selector: string): T {
        const element = this.root.querySelector<T>(selector);
        if (!element) {
            throw new Error(`The calculator's markup lacks ${selector}.`);
        }

        return element;
    }

    /** A text template of the root's data attributes ("{0} wählen"). */
    private template(name: string): string {
        return this.root.dataset[name] ?? "{0}";
    }

    /** Shows the element with the text, or hides it for null. */
    private say(selector: string, text: string | null): void {
        const element = this.required<HTMLElement>(selector);
        element.hidden = text === null;
        element.textContent = text ?? "";
    }

    private users(): number {
        return Math.min(this.max + 1, Math.max(this.min, Number(this.range.value)));
    }

    private plan(): CalcPlan {
        return this.plans.find((p) => this.radios.some((r) => r.checked && r.value === p.key)) ?? this.first;
    }

    private step(delta: number): void {
        this.range.value = String(Math.min(this.max + 1, Math.max(this.min, this.users() + delta)));
        this.render();
    }

    private choose(plan: CalcPlan | null): void {
        const radio = this.radios.find((r) => r.value === plan?.key);
        if (radio) {
            radio.checked = true;
            this.render();
        }
    }

    /** The cheapest package that takes the users, or null while the chosen one takes them. */
    private suggestion(): CalcPlan | null {
        const users = this.users();
        const plan = this.plan();
        if (fits(plan, users)) {
            return null;
        }

        const suggested = cheapestFitting(this.plans, users);
        return suggested?.key === plan.key ? null : suggested;
    }

    private render(): void {
        const users = this.users();
        const plan = this.plan();
        const room = fits(plan, users);
        const month = monthly(plan, users);
        this.output.textContent = users > this.max ? `${this.max}+` : String(users);
        this.say("[data-request-box]", isPriced(plan) ? null : fill(this.template("request"), plan.name));
        this.say("[data-over-box]", isPriced(plan) && !room ? fill(this.template("over"), plan.name, String(plan.max ?? this.max)) : null);
        this.required<HTMLElement>("[data-costs]").hidden = month === null;
        if (month !== null) {
            this.required<HTMLElement>("[data-month]").textContent = this.money.format(month);
            this.required<HTMLElement>("[data-year]").textContent = this.money.format(month * 12);
        }

        this.renderTrial(plan, room);
        this.renderSuggestion();
    }

    /** The trial link follows the chosen package; none for one on request or one the users do not fit into. */
    private renderTrial(plan: CalcPlan, room: boolean): void {
        const link = this.required<HTMLAnchorElement>("[data-try-link]");
        link.hidden = plan.trial === null || !room;
        if (plan.trial !== null) {
            link.href = plan.trial;
            link.textContent = fill(this.template("try"), plan.name);
        }
    }

    private renderSuggestion(): void {
        const suggested = this.suggestion();
        this.required<HTMLElement>("[data-suggest-box]").hidden = suggested === null;
        if (suggested) {
            this.required<HTMLElement>("[data-suggest-text]").textContent = fill(this.template("suggest"), suggested.name);
            this.required<HTMLButtonElement>("[data-suggest-btn]").textContent = fill(this.template("choose"), suggested.name);
        }
    }
}

/** The one init: starts what the page has and the visitor allows. */
function init(): void {
    const root = document.documentElement;
    root.classList.add("js");
    if (!prefersReducedMotion()) {
        new RevealMotion(root).start();
    }

    new StepHighlight([...document.querySelectorAll<HTMLElement>("[data-step]")]).start();

    const menu = document.querySelector<HTMLDetailsElement>("[data-menu]");
    if (menu) {
        new MenuCloser(menu).start();
    }

    const calculator = document.querySelector<HTMLElement>("[data-calc]");
    if (calculator) {
        new PriceCalculator(calculator).start();
    }
}

init();
